/**
 * Helpers de exportacao dos relatorios financeiros (DRE, Fluxo de Caixa,
 * Rentabilidade) — Sprint M1 Quick Wins (2026-05-25).
 *
 * Converte payload dos endpoints `/api/financeiro/dre`,
 * `/api/financeiro/fluxo-caixa`, `/api/financeiro/rentabilidade` para o
 * shape `{titulo, resumo, secoes}` que `salvarPDF` / `salvarExcel` /
 * `salvarCSV` (em `utils/exportacao`) consomem.
 *
 * Centralizar a transformacao aqui evita repeticao nos 3 componentes e
 * facilita testar separadamente.
 */

import { salvarCSV, salvarExcel, salvarPDF } from "../../../utils/exportacao";


const SAVERS = {
  pdf: salvarPDF,
  excel: salvarExcel,
  csv: salvarCSV,
};


/**
 * Dispatcher generico — recebe shape final + formato, chama a funcao certa.
 */
function _despachar(formato, shape) {
  const saver = SAVERS[formato];
  if (!saver) throw new Error(`Formato desconhecido: ${formato}`);
  return saver(shape);
}


function _fmtPeriodo(periodo) {
  if (!periodo) return "";
  const ini = periodo.inicio || "";
  const fim = periodo.fim || "";
  return `${ini} a ${fim}`;
}


/**
 * DRE — Demonstrativo do Resultado em regime competencia.
 *
 * Payload de `/api/financeiro/dre` tem:
 *   periodo, receitas: {total, por_categoria:[{categoria, total, pct}]},
 *   despesas: idem, lucro_bruto, margem_bruta, lancamentos_count
 */
export function exportarDre(formato, payload) {
  const periodoStr = _fmtPeriodo(payload?.periodo);

  const resumo = [
    { label: "Período", value: periodoStr, type: "text" },
    { label: "Receitas totais", value: Number(payload?.receitas?.total || 0), type: "currency" },
    { label: "Despesas totais", value: Number(payload?.despesas?.total || 0), type: "currency" },
    { label: "Lançamentos considerados", value: payload?.lancamentos_count || 0, type: "number" },
  ];

  // Cascata DRE oficial (FIN-5) — subtotais calculados no backend. Quando
  // presente, exporta a cascata; senao cai no lucro_bruto retrocompat.
  const casc = payload?.cascata_dre;
  if (casc) {
    resumo.push(
      { label: "Receita líquida", value: Number(casc.receita_liquida || 0), type: "currency" },
      { label: "Lucro bruto", value: Number(casc.lucro_bruto || 0), type: "currency" },
      { label: "Resultado operacional", value: Number(casc.resultado_operacional || 0), type: "currency" },
      { label: "Resultado líquido", value: Number(casc.resultado_liquido || 0), type: "currency" },
      { label: "Margem líquida %", value: Number(casc.margem_liquida || 0), type: "percentage" },
    );
  } else {
    resumo.push(
      { label: "Lucro bruto", value: Number(payload?.lucro_bruto || 0), type: "currency" },
      { label: "Margem bruta %", value: Number(payload?.margem_bruta || 0), type: "percentage" },
    );
  }

  const colunasCat = [
    { key: "categoria", label: "Categoria", type: "text" },
    { key: "total", label: "Total", type: "currency" },
    { key: "pct", label: "% do grupo", type: "percentage" },
  ];

  const secoes = [
    {
      id: "receitas",
      label: "Receitas por categoria",
      columns: colunasCat,
      rows: (payload?.receitas?.por_categoria || []).map((r) => ({
        categoria: r.categoria,
        total: Number(r.total),
        pct: Number(r.pct),
      })),
    },
    {
      id: "despesas",
      label: "Despesas por categoria",
      columns: colunasCat,
      rows: (payload?.despesas?.por_categoria || []).map((r) => ({
        categoria: r.categoria,
        total: Number(r.total),
        pct: Number(r.pct),
      })),
    },
  ];

  return _despachar(formato, {
    titulo: `DRE ${periodoStr}`,
    resumo,
    secoes,
  });
}


/**
 * Fluxo de Caixa projetado com buckets temporais.
 *
 * Payload tem: periodo, granularidade, saldo_inicial_periodo, buckets:[]
 */
export function exportarFluxoCaixa(formato, payload) {
  const periodoStr = _fmtPeriodo(payload?.periodo);

  const resumo = [
    { label: "Período", value: periodoStr, type: "text" },
    { label: "Granularidade", value: payload?.granularidade || "", type: "text" },
    { label: "Saldo inicial", value: Number(payload?.saldo_inicial_periodo || 0), type: "currency" },
    { label: "Buckets gerados", value: (payload?.buckets || []).length, type: "number" },
  ];

  const colunas = [
    { key: "label", label: "Período", type: "text" },
    { key: "entradas_realizadas", label: "Entradas realizadas", type: "currency" },
    { key: "saidas_realizadas", label: "Saídas realizadas", type: "currency" },
    { key: "entradas_projetadas", label: "Entradas projetadas", type: "currency" },
    { key: "saidas_projetadas", label: "Saídas projetadas", type: "currency" },
    { key: "saldo_periodo", label: "Saldo do período", type: "currency" },
    { key: "saldo_acumulado", label: "Saldo acumulado", type: "currency" },
  ];

  const rows = (payload?.buckets || []).map((b) => ({
    label: b.label,
    entradas_realizadas: Number(b.entradas_realizadas),
    saidas_realizadas: Number(b.saidas_realizadas),
    entradas_projetadas: Number(b.entradas_projetadas),
    saidas_projetadas: Number(b.saidas_projetadas),
    saldo_periodo: Number(b.saldo_periodo),
    saldo_acumulado: Number(b.saldo_acumulado),
  }));

  return _despachar(formato, {
    titulo: `Fluxo de Caixa ${periodoStr}`,
    resumo,
    secoes: [{
      id: "buckets",
      label: "Fluxo por periodo",
      columns: colunas,
      rows,
    }],
  });
}


/**
 * Rentabilidade por pedido — ranking ordenado por lucro liquido.
 *
 * Payload tem: periodo, totais:{receita, tvc, oe_rateado, lucro_liquido,
 * margem_media}, pedidos:[{numero, cliente, situacao, receita, tvc,
 * oe_rateado, lucro_liquido, margem_liquida}]
 */
export function exportarRentabilidade(formato, payload) {
  const periodoStr = _fmtPeriodo(payload?.periodo);

  const totais = payload?.totais || {};
  const resumo = [
    { label: "Período", value: periodoStr, type: "text" },
    { label: "Pedidos analisados", value: payload?.pedidos_count || 0, type: "number" },
    { label: "Receita total", value: Number(totais.receita || 0), type: "currency" },
    { label: "TVC total", value: Number(totais.tvc || 0), type: "currency" },
    { label: "OE rateado", value: Number(totais.oe_rateado || 0), type: "currency" },
    { label: "Lucro líquido total", value: Number(totais.lucro_liquido || 0), type: "currency" },
    { label: "Margem média %", value: Number(totais.margem_media || 0), type: "percentage" },
  ];

  const colunas = [
    { key: "numero", label: "Pedido", type: "text" },
    { key: "cliente", label: "Cliente", type: "text" },
    { key: "situacao", label: "Situação", type: "status" },
    { key: "receita", label: "Receita", type: "currency" },
    { key: "tvc", label: "TVC (CAD)", type: "currency" },
    { key: "oe_rateado", label: "OE rateado", type: "currency" },
    { key: "lucro_liquido", label: "Lucro líquido", type: "currency" },
    { key: "margem_liquida", label: "Margem %", type: "percentage" },
  ];

  const rows = (payload?.pedidos || []).map((p) => ({
    numero: p.numero,
    cliente: p.cliente,
    situacao: p.situacao,
    receita: Number(p.receita),
    tvc: Number(p.tvc),
    oe_rateado: Number(p.oe_rateado),
    lucro_liquido: Number(p.lucro_liquido),
    margem_liquida: Number(p.margem_liquida),
  }));

  return _despachar(formato, {
    titulo: `Rentabilidade ${periodoStr}`,
    resumo,
    secoes: [{
      id: "pedidos",
      label: "Pedidos por rentabilidade",
      columns: colunas,
      rows,
    }],
  });
}
